import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Activity, LayoutGrid, Menu } from "lucide-react";
import { useAlerts } from "@/shared/api/hooks/alerts";
import { isUnresolved } from "@/shared/lib/alertScope";
import { cn } from "@/shared/lib/cn";
import { MoreSheet } from "./MoreSheet";

/**
 * Phone navigation.
 *
 * Below `lg` the sidebar is gone, so the three routes an operator actually
 * opens on a phone — the board, the alerts, the nodes — get permanent tabs,
 * and everything else lives one tap away in a sheet. Four targets is the most
 * a thumb can hit reliably at this width.
 *
 * Sits at z-40: above page content, deliberately below the alarm dock (85/90)
 * and toasts (120) so an incident overlay is never hidden behind navigation.
 */
export function BottomTabBar() {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { data } = useAlerts();

  // 배지와 경보 화면 기본 필터는 **같은 정의(미해제)** 를 써야 한다 —
  // 배지 27인데 목록이 비어 있던 사고(2026-08-31)의 원인이 정의 불일치였다.
  const openAlerts = (data?.results ?? []).filter(isUnresolved).length;

  return (
    <>
      <nav
        aria-label={t("nav.tabPrimary")}
        className={cn(
          "lg:hidden fixed inset-x-0 bottom-0 z-40",
          "bg-[var(--bg-elevated)] border-t border-[var(--separator)]",
          "grid grid-cols-4",
        )}
        style={{ paddingBottom: "var(--tabbar-safe-b)" }}
      >
        <Tab to="/" icon={LayoutGrid} label={t("nav.tabControl")} end />
        <Tab to="/alerts" icon={AlertTriangle} label={t("nav.tabAlerts")} badge={openAlerts} />
        <Tab to="/sensors" icon={Activity} label={t("nav.tabSensors")} />
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className={cn(
            "relative flex flex-col items-center justify-center gap-0.5",
            "min-h-[var(--tabbar-content-h)] text-[10.5px] text-[var(--fg-muted)]",
            "active:bg-[var(--bg-active)] transition-colors duration-[var(--motion-fast)]",
          )}
        >
          <Menu size={21} strokeWidth={1.75} />
          <span>{t("nav.tabMore")}</span>
        </button>
      </nav>

      <MoreSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}

function Tab({
  to,
  icon: Icon,
  label,
  badge,
  end,
}: {
  to: string;
  icon: typeof LayoutGrid;
  label: string;
  badge?: number;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        cn(
          "relative flex flex-col items-center justify-center gap-0.5",
          "min-h-[var(--tabbar-content-h)] text-[10.5px]",
          "active:bg-[var(--bg-active)] transition-colors duration-[var(--motion-fast)]",
          isActive ? "text-[var(--accent)] font-semibold" : "text-[var(--fg-muted)]",
        )
      }
    >
      <span className="relative">
        <Icon size={21} strokeWidth={1.75} />
        {badge != null && badge > 0 && (
          <span
            className={cn(
              "absolute -top-1.5 -right-2.5 min-w-[16px] h-[16px] px-1 rounded-full",
              "grid place-items-center text-[9.5px] font-bold tabular-nums",
            )}
            style={{ background: "var(--danger-solid)", color: "var(--accent-fg)" }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      <span>{label}</span>
    </NavLink>
  );
}
